import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { areAllOptionsSet } from "theme/modules/AddToCart/areAllOptionsSet";
import { PrimaryButton } from "theme/components/atoms/Button";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import useSelectedProductWithConfigurableOptions from "theme/pages/Product/useSelectedProductWithConfigurableOptions";
import ConfigurableOptions from "theme/modules/Cart/CartItem/CartItemOptionsUpdater/ConfigurableOptions";
import { H2 } from "theme/components/atoms/Typography/Heading";
import Form from "theme/components/atoms/Form/Form";
import FormTitle from "theme/components/molecules/Form/FormTitle";
import useProductBySkuLoader from "theme/pages/Product/useProductBySkuLoader";
import Stack from "theme/components/atoms/Layout/Stack";

import InlineAlert from "theme/components/molecules/Alert/InlineAlert";
import Spinner from "theme/components/atoms/Spinner";
import ErrorAlert from "theme/components/molecules/Alert/ErrorAlert";
import WarnAlert from "theme/components/molecules/Alert/WarnAlert";

export const LoadingMessage = () => (
  <InlineAlert appearance="info">
    <Spinner dimensions={{ display: "inline" }} />
    <span className="select-product-configuration-modal__loading-message">
      <FormattedMessage
        id="modules.RequisitionList.ProductConfigurationModal.loadingProduct"
        defaultMessage="Loading..."
      />
    </span>
  </InlineAlert>
);

export const ErrorMessage = () => (
  <ErrorAlert>
    <FormattedMessage
      id="modules.RequisitionList.ProductConfigurationModal.errorLoadingProduct"
      defaultMessage="Oops, something went wrong!"
    />
  </ErrorAlert>
);

export const NotAllOptionsSet = () => (
  <WarnAlert>
    <FormattedMessage
      id="modules.RequisitionList.ProductConfigurationModal.notAllOptionsSet"
      defaultMessage="Not all options have been selected"
    />
  </WarnAlert>
);

export const NotFoundMessage = () => (
  <ErrorAlert>
    <FormattedMessage
      id="modules.RequisitionList.ProductConfigurationModal.productNotFound"
      defaultMessage="Product not found!"
    />
  </ErrorAlert>
);

/** @type {React.FunctionComponent<PropTypes.InferProps<propTypes>>} */
const ProductConfigurationModalContent = ({
  product,
  onConfiurationsSelected,
  selectedConfigurableOptions,
  buttonLabel,
}) => {
  const {
    loading,
    error,
    product: parentProduct,
  } = useProductBySkuLoader(product.parent_product?.sku || product.sku);

  const { selectedProduct, selectedOptions, setOption } =
    useSelectedProductWithConfigurableOptions(
      parentProduct,
      selectedConfigurableOptions
    );

  const formRef = useRef();
  const [showNotAllOptionsSelected, setShowNotAllOptionsSelected] =
    useState(false);

  const onChangeOptions = useCallback(() => {
    const model = formRef.current.getModel();
    Object.keys(model)
      .filter(
        (key) =>
          key.indexOf("custom:") !== 0 && typeof model[key] !== "undefined"
      )
      .forEach((optionId) =>
        setOption(optionId, model[optionId].value || model[optionId])
      );
  }, [setOption]);

  const allOptionsSet = useMemo(
    () => selectedProduct && areAllOptionsSet(selectedProduct, selectedOptions),
    [selectedProduct, selectedOptions]
  );

  useEffect(() => {
    if (selectedProduct?.options?.length === 0) {
      onConfiurationsSelected({});
    }
  }, [onConfiurationsSelected, selectedProduct?.options]);

  if (loading) {
    return <LoadingMessage />;
  }

  if (error) {
    return <ErrorMessage />;
  }

  if (!selectedProduct) {
    return <NotFoundMessage />;
  }

  if (!selectedProduct.options) {
    return null;
  }

  return (
    <Form
      setRef={(form) => (formRef.current = form)}
      onValidSubmit={() => onConfiurationsSelected(selectedOptions)}
      onChange={onChangeOptions}
    >
      <Stack>
        <FormTitle>
          <H2>
            <FormattedMessage
              id="modules.RequisitionList.ProductConfigurationModal.title"
              defaultMessage="Select product options"
            />
          </H2>
        </FormTitle>

        <ConfigurableOptions
          key="options"
          options={selectedProduct.options}
          selectedOptions={selectedOptions}
        />

        {!allOptionsSet && showNotAllOptionsSelected ? (
          <NotAllOptionsSet />
        ) : null}

        <div className="select-product-configuration-modal__button">
          <PrimaryButton
            type="submit"
            state={allOptionsSet ? "default" : "disabled"}
            onDisableClick={() => setShowNotAllOptionsSelected(true)}
          >
            {buttonLabel}
          </PrimaryButton>
        </div>
      </Stack>
    </Form>
  );
};

const propTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string.isRequired,
  }).isRequired,
  onConfiurationsSelected: PropTypes.func.isRequired,
  selectedConfigurableOptions: PropTypes.object,
  buttonLabel: PropTypes.any.isRequired,
};
ProductConfigurationModalContent.propTypes = propTypes;

export default ProductConfigurationModalContent;
